import NimbleWordmarkLogo from "@/app/(discover)/components/nimble-wordmark-logo";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface AuthPageLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  heroTitle: string;
  heroDescription: string;
  features: Feature[];
  heroEyebrow?: string;
  headerSlot?: React.ReactNode;
  belowCardSlot?: React.ReactNode;
}

export function AuthPageLayout({
  children,
  title,
  subtitle,
  heroTitle,
  heroDescription,
  features,
  heroEyebrow,
  headerSlot,
  belowCardSlot,
}: AuthPageLayoutProps) {
  return (
    <div className="grid flex-auto min-h-full lg:grid-cols-2">
      {/* Left Column - Form */}
      <main className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <header className="mb-6 text-center">
              <div className="flex items-center justify-center mb-4">
                <NimbleWordmarkLogo className="h-10 w-auto" />
              </div>
              {headerSlot}
              <h1 className="text-2xl font-semibold text-foreground mb-2">
                {title}
              </h1>
              <p className="text-sm text-ink-subtle">{subtitle}</p>
            </header>
            <div className="border border-line-faint rounded-lg shadow-sm p-6">
              {children}
            </div>
            {belowCardSlot && <div className="mt-4">{belowCardSlot}</div>}
          </div>
        </div>

        <footer className="text-center text-xs text-ink-subtle">
          <p>© {new Date().getFullYear()} Nimble. All rights reserved.</p>
        </footer>
      </main>

      {/* Right Column - Branding */}
      <aside className="bg-muted relative hidden lg:block overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-secondary/10 to-secondary/5">
          <div className="h-full w-full flex flex-col items-center justify-center p-12">
            <div className="max-w-lg space-y-6">
              <div className="text-center space-y-3">
                {heroEyebrow && (
                  <span className="inline-block rounded-md bg-secondary/10 px-3 py-1 text-sm font-semibold text-secondary">
                    {heroEyebrow}
                  </span>
                )}
                <h2 className="text-2xl font-bold text-foreground">
                  {heroTitle}
                </h2>
                <p className="text-base text-muted-foreground">
                  {heroDescription}
                </p>
              </div>

              <div className="grid gap-3 mt-6">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 bg-card/80 backdrop-blur-sm rounded-lg p-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center text-secondary">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1 text-sm">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
